import {
  ColorScheme,
  ColorSchemeProvider,
  MantineProvider,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { NotificationsProvider, showNotification } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { queryClient } from 'lib/react-query';
import { setGlobalApiErrorHandler } from 'utils/apiErrorHandler';

function GlobalApiErrorHandler() {
  useEffect(() => {
    setGlobalApiErrorHandler((message: string) => {
      showNotification({
        title: 'Error',
        message,
        color: 'red',
        autoClose: 5000,
      });
    });
    return () => setGlobalApiErrorHandler(null);
  }, []);
  return null;
}

type AppProviderProps = {
  children: React.ReactNode;
};

function AppProviders({ children }: AppProviderProps) {
  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: 'flowboard-color-scheme',
    defaultValue: 'light',
    getInitialValueInEffect: true,
  });

  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'));

  useEffect(() => {
    if (colorScheme === 'dark') {
      document.body.classList.add('full-dark');
    } else {
      document.body.classList.remove('full-dark');
    }
  }, [colorScheme]);

  return (
    <ColorSchemeProvider
      colorScheme={colorScheme}
      toggleColorScheme={toggleColorScheme}
    >
      <MantineProvider
        theme={{
          colorScheme,
          primaryColor: 'violet',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          headings: {
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          },
        }}
        withCSSVariables
        withGlobalStyles
        withNormalizeCSS
      >
        {colorScheme === 'dark' && (
          <style>
            {`
              body.full-dark, 
              .full-dark .mantine-Navbar-root,
              .full-dark .mantine-Header-root,
              .full-dark .mantine-AppShell-main,
              .full-dark .mantine-Aside-root {
                background-color: #000000 !important;
              }

              .full-dark .mantine-Card-root,
              .full-dark .mantine-Paper-root,
              .full-dark .mantine-Modal-content,
              .full-dark .mantine-Menu-dropdown,
              .full-dark .mantine-Popover-dropdown,
              .full-dark .mantine-Drawer-content {
                background-color: #000000 !important;
                border: 1px solid #373A40 !important;
              }

              .full-dark .mantine-Text-root, 
              .full-dark .mantine-Title-root,
              .full-dark .mantine-NavLink-label,
              .full-dark .mantine-Navbar-root a,
              .full-dark .mantine-Navbar-root span,
              .full-dark h1, .full-dark h2, .full-dark h3, .full-dark h4 {
                color: #FFFFFF !important;
              }

              .full-dark .mantine-Button-subtle,
              .full-dark .mantine-Button-ghost,
              .full-dark .mantine-Button-outline {
                color: #FFFFFF !important;
              }
              
              .full-dark .mantine-Button-root:not([data-disabled]):hover {
                background-color: #1A1B1E !important;
              }

              .full-dark .mantine-Input-input,
              .full-dark .mantine-Textarea-input,
              .full-dark .mantine-Select-input {
                color: #FFFFFF !important;
                background-color: #000000 !important;
                border: 1px solid #4D4F56 !important;
              }

              .full-dark input::placeholder,
              .full-dark textarea::placeholder {
                color: #ced4da !important;
              }

              .full-dark [data-dimmed],
              .full-dark .mantine-Text-dimmed {
                color: #ced4da !important;
              }

              .full-dark .mantine-Table-root tr,
              .full-dark .mantine-Table-root td,
              .full-dark .mantine-Table-root th {
                background-color: #000000 !important;
                color: #FFFFFF !important;
                border-bottom: 1px solid #373A40 !important;
              }

              .full-dark .mantine-Menu-item {
                color: #FFFFFF !important;
                background-color: #000000 !important;
              }
              .full-dark .mantine-Menu-item:hover {
                background-color: #1A1B1E !important;
              }

              .full-dark .mantine-Select-dropdown {
                background-color: #000000 !important;
                color: #ffffff !important;
                border: 1px solid #4D4F56 !important;
              }

              .full-dark .mantine-Select-item {
                background-color: #000000 !important;
                color: #ffffff !important;
              }

              .full-dark .mantine-Select-item:hover {
                background-color: #1A1B1E !important;
              }

              /* ⭐ FIX: GRAY CLOSED SELECT BOX */
              .full-dark .mantine-Select-root .mantine-Input-wrapper {
                background-color: #000000 !important;
                border: 1px solid #4D4F56 !important;
              }

              .full-dark .mantine-Select-root .mantine-Input-wrapper input {
                color: #ffffff !important;
              }

              .full-dark .mantine-Select-root .mantine-Input-rightSection {
                color: #ffffff !important;
              }
            `}
          </style>
        )}

        <NotificationsProvider autoClose={3000} position="bottom-center">
          <GlobalApiErrorHandler />
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>{children}</BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </NotificationsProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  );
}

export default AppProviders;
